import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { X, Trash2, Calendar, Clock } from "lucide-react";
import { deleteMealAvailabilityById, getMealAvailability } from "../../service/mealService";
import { formatDateForApi, formatDateForDisplay } from "../../utils/dateUtils";

const MealAvailabilityModal = ({ 
  isOpen, 
  onClose, 
  selectedDate, 
  restaurantId 
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const selectedDateStr = useMemo(() => formatDateForApi(selectedDate), [selectedDate]);

  // Fetch live availability data for the selected date
  const { 
    data: availabilities = [], 
    isLoading: isLoadingAvailabilities 
  } = useQuery({
    queryKey: ["mealAvailabilityForDate", restaurantId, selectedDateStr],
    queryFn: async () => {
      if (!selectedDateStr || !restaurantId) return [];
      
      const response = await getMealAvailability({
        restaurantId,
        startDate: selectedDateStr,
        endDate: selectedDateStr
      });
      
      return response || [];
    },
    enabled: isOpen && !!selectedDateStr && !!restaurantId,
    staleTime: 0, // Always fetch fresh data
  });

  const deleteAvailabilityMutation = useMutation({
    mutationFn: deleteMealAvailabilityById,
    onSuccess: () => {
      // Invalidate and refetch availability data
      queryClient.invalidateQueries({
        queryKey: ["mealAvailability", restaurantId]
      });
      // Also invalidate the specific date query
      queryClient.invalidateQueries({
        queryKey: ["mealAvailabilityForDate", restaurantId, selectedDateStr]
      });
    },
    onError: (error) => {
      console.error("Failed to delete availability:", error);
      // Could add toast notification here
    },
  });

  const handleDeleteAvailability = (availabilityId) => {
    deleteAvailabilityMutation.mutate(availabilityId);
  };

  const formatDate = (date) => {
    return formatDateForDisplay(date);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t("mealAvailability.mealAvailabilityFor")}
              </h2>
              <p className="text-sm text-gray-600">
                {formatDate(selectedDate)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoadingAvailabilities ? (
            <div className="p-8 text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-gray-600">{t("mealAvailability.loading")}</span>
              </div>
            </div>
          ) : availabilities.length > 0 ? (
            <div className="p-6 space-y-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                <Clock className="h-4 w-4" />
                <span>
                  {t("mealAvailability.availableMealsCount", { 
                    count: availabilities.length 
                  })}
                </span>
              </div>
              
              {availabilities.map((availability) => (
                <div
                  key={availability.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">
                      {availability.mealName}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t("mealAvailability.mealId")}: {availability.mealId}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => handleDeleteAvailability(availability.id)}
                    disabled={deleteAvailabilityMutation.isPending}
                    className="ml-3 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t("mealAvailability.removeMealAvailability")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("mealAvailability.noMealsAvailable")}
              </h3>
              <p className="text-gray-500">
                {t("mealAvailability.noMealsAvailableDescription")}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoadingAvailabilities && availabilities.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t("common.close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MealAvailabilityModal;
